import React, { FormHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useFormik } from 'formik'
import parse from 'html-react-parser'

import Button from '@/components/base/button'
import {
  ConsentField,
  DateField,
  FileUploadField,
  HtmlField,
  MultiChoiceField,
  RadioField,
  SelectField,
  TextField
} from '@/components/base/fields'
import Spacer from '@/components/sections/spacer'
import { useLenis } from '@/contexts/lenis'
import { isHappa } from '@/hooks'
import { useLocaleStore } from '@/store/locale'
import { convertJsontoFormData, createInitialValues, createYupSchema } from '@/utils/form'
import { getVisibleFieldIds, isFieldVisible } from '@/utils/form/conditional-logic'
import { parseGravityDateValue } from '@/utils/form/date-format'
import { API } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import 'dayjs/locale/it'

import { WPCF7Form } from '@/types'

export type FormProps = Omit<FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  form_id: string
  submit: {
    text: string
    color?: 'inherit' | 'error' | 'success' | 'primary' | 'secondary' | 'info' | 'warning'
  }
  fields: WPCF7Form['form']
  customEndpoint?: boolean
  useGravity?: boolean
  onSuccessSubmit?: () => void
  bypassPasswordValidationEmail?: string
  onSubmit?: (_values: Record<string, any>) => boolean | void | Promise<boolean | void>
}

const Form = ({
  form_id,
  fields = [],
  submit,
  customEndpoint,
  useGravity = true,
  onSubmit,
  onSuccessSubmit,
  bypassPasswordValidationEmail,
  ...props
}: FormProps) => {
  const [statusMessage, setStatusMessage] = useState('&nbsp;')
  const [passwordVisibility, setPasswordVisibility] = useState<Record<string, boolean>>({})
  const [formValues, setFormValues] = useState<Record<string, any>>({})
  const formRef = useRef<HTMLFormElement>(null)
  const shouldScrollToError = useRef(false)
  const { scrollTo } = useLenis()
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const normalizeSubmitValues = useCallback(
    (values: Record<string, any>) => {
      return fields.reduce(
        (acc, field) => {
          const inputKey = `input_${field.id}`
          const fieldValue = values[inputKey]

          if (field.type === 'consent') {
            const subValue = values[`input_${field.id}_1`]
            const checked = subValue === '1' || subValue === true || Boolean(fieldValue)
            acc[inputKey] = checked
            acc[`input_${field.id}_1`] = checked ? '1' : '0'
            return acc
          }

          if (field.type === 'date' && typeof fieldValue === 'string' && fieldValue.trim()) {
            const parsedDate = parseGravityDateValue(fieldValue, field.dateFormat)
            acc[inputKey] = parsedDate ? parsedDate.format('YYYY-MM-DD') : fieldValue
            return acc
          }

          acc[inputKey] = fieldValue
          return acc
        },
        {} as Record<string, any>
      )
    },
    [fields]
  )

  // Compute visible field IDs based on current form values and conditional logic
  const visibleFieldIds = useMemo(() => {
    return getVisibleFieldIds(fields, formValues)
  }, [fields, formValues])

  // Create validation schema that respects conditional visibility
  const validationSchema = useMemo(() => {
    return createYupSchema(fields, currentLocale, visibleFieldIds, bypassPasswordValidationEmail)
  }, [fields, currentLocale, visibleFieldIds, bypassPasswordValidationEmail])

  const getFieldContainer = useCallback(
    (errorKey: string) => {
      if (!formRef.current) return null

      const matchedField = fields.find(
        (field) => `input_${field.id}` === errorKey || field.adminLabel === errorKey
      )

      return (
        formRef.current.querySelector<HTMLElement>(`[data-field-name="${errorKey}"]`) ||
        (matchedField
          ? formRef.current.querySelector<HTMLElement>(`[data-field-id="${matchedField.id}"]`)
          : null) ||
        formRef.current.querySelector<HTMLElement>(`[name="${errorKey}"]`)
      )
    },
    [fields]
  )

  // Scroll to first invalid field when errors change after submit attempt
  const scrollToFirstError = useCallback(
    (errors: Record<string, any>) => {
      const errorKeys = Object.keys(errors)
      if (errorKeys.length === 0 || !formRef.current) return

      const firstErrorKey = errorKeys[0]
      const errorElement = getFieldContainer(firstErrorKey)

      if (errorElement) {
        scrollTo(errorElement, { offset: -window.innerHeight / 3 })

        const focusTarget = errorElement.matches('input, textarea, select, button')
          ? errorElement
          : errorElement.querySelector<HTMLElement>(
              'input, textarea, select, button, [role="combobox"], [tabindex]'
            )

        focusTarget?.focus?.()
      }
    },
    [getFieldContainer, scrollTo]
  )

  async function sendContactForm(sendData: any) {
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    const response = await axios.post(
      `${API.GRAVITY_FORM}/${form_id}/submissions`,
      convertJsontoFormData(sendData),
      { headers }
    )
    return response
  }

  const formik = useFormik<any>({
    initialValues: createInitialValues(fields) || {},
    validationSchema,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: (values, { setStatus, setSubmitting, resetForm }) => {
      const submitForm = async () => {
        let submittedSuccessfully = false
        const normalizedValues = normalizeSubmitValues(values)

        try {
          if (customEndpoint) {
            const customSubmitResult = await onSubmit?.(normalizedValues)
            if (customSubmitResult === false) return
          }
          if (useGravity) {
            const gravityResponse = await sendContactForm(normalizedValues)
            const confirmationMessage = gravityResponse?.data?.confirmation_message
            if (confirmationMessage) {
              setStatusMessage(confirmationMessage)
            }
          }
          submittedSuccessfully = true
          setStatus('success')
          if (onSuccessSubmit) onSuccessSubmit?.()
        } catch (error: any) {
          if (error.response?.status === 400 && error.response.data?.validation_messages) {
            formik.setErrors(error.response.data.validation_messages)
            setStatusMessage('Correggi gli errori nei campi')
            setStatus('error')
          } else {
            const message =
              error.response?.data?.message ||
              error.response?.data?.error ||
              error.message ||
              "Errore nell'invio del form"
            setStatusMessage(message)
            setStatus('error')
          }
        } finally {
          if (submittedSuccessfully) {
            setTimeout(() => {
              resetForm()
              setStatusMessage('&nbsp;')
              setStatus('')
              setSubmitting(false)
            }, 3000)
          } else {
            setSubmitting(false)
          }
        }
      }
      submitForm()
    }
  })

  // Scroll to first invalid field only when triggered by a submit attempt
  useEffect(() => {
    if (!shouldScrollToError.current) return
    if (Object.keys(formik.errors).length > 0) {
      shouldScrollToError.current = false
      requestAnimationFrame(() => scrollToFirstError(formik.errors))
    }
  }, [formik.errors, scrollToFirstError])

  // Sync form values for conditional logic evaluation
  useEffect(() => {
    setFormValues(formik.values)
  }, [formik.values])

  // Check if a field should be visible based on conditional logic
  const checkFieldVisibility = (field: WPCF7Form['form'][0]) => {
    return isFieldVisible(field, formik.values)
  }

  const renderFieldContent = (field: WPCF7Form['form'][0]) => {
    switch (field.type) {
      case 'consent':
        return <ConsentField field={field} formik={formik} />
      case 'select':
      case 'multiselect':
        return <SelectField field={field} formik={formik} allFields={fields} />
      case 'date':
        return <DateField field={field} formik={formik} />
      case 'html':
        return <HtmlField field={field} />
      case 'fileupload':
        return <FileUploadField field={field} formik={formik} />
      case 'multi_choice':
        return <MultiChoiceField field={field} formik={formik} />
      case 'radio':
        return <RadioField field={field} formik={formik} />
      default:
        return (
          <TextField
            field={field}
            formik={formik}
            passwordVisibility={passwordVisibility}
            setPasswordVisibility={setPasswordVisibility}
          />
        )
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
      <Box
        ref={formRef}
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          shouldScrollToError.current = true
          formik.handleSubmit(e)
        }}
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          columnGap: { md: 4 },
          rowGap: 4
        }}
        {...props}
      >
        {(() => {
          const jsxElements: React.JSX.Element[] = []
          let currentGroup: typeof fields = []
          let keyIndex = 0
          fields?.forEach((field) => {
            if (field.type === 'section') {
              if (currentGroup.length > 0) {
                jsxElements.push(
                  <Box
                    key={`group-${keyIndex++}`}
                    sx={(theme) => ({
                      gridColumn: 'span 12',
                      p: 4,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(12, 1fr)',
                      columnGap: { md: 4 },
                      rowGap: 4,
                      borderRadius: theme.shape.borderRadius4,
                      backgroundColor: theme.palette.surface.background,
                      boxShadow: theme.shadows[2],
                      mb: 2
                    })}
                  >
                    {field.label ? (
                      <Box sx={{ gridColumn: 'span 12' }}>
                        <Typography variant="h6" color={isHappa ? 'primary.main' : 'text.primary'}>
                          {field.label}
                        </Typography>
                        {field.description ? (
                          <>
                            <Spacer size="xxs" />
                            <Typography variant="textRegular" color="text.primary">
                              {field.description}
                            </Typography>
                          </>
                        ) : null}
                        <Spacer size="xs" />
                      </Box>
                    ) : null}
                    {currentGroup.map((f, i) => {
                      const fieldIsVisible = checkFieldVisibility(f)
                      return (
                        <Box
                          key={i}
                          data-field-id={f.id}
                          data-field-name={`input_${f.id}`}
                          sx={{
                            gridColumn: {
                              xs: 'span 12',
                              md: `span ${f.layoutGridColumnSpan}`
                            },
                            display: f.type === 'hidden' || !fieldIsVisible ? 'none' : 'block'
                          }}
                        >
                          {renderFieldContent(f)}
                        </Box>
                      )
                    })}
                  </Box>
                )
                currentGroup = []
              }
            } else {
              currentGroup.push(field)
            }
          })
          // Do not wrap the final group if no section follows
          if (currentGroup.length > 0) {
            jsxElements.push(
              ...currentGroup.map((f, i) => {
                const fieldIsVisible = checkFieldVisibility(f)
                return (
                  <Box
                    key={`field-${keyIndex++}-${i}`}
                    data-field-id={f.id}
                    data-field-name={`input_${f.id}`}
                    sx={{
                      gridColumn: {
                        xs: 'span 12',
                        md: `span ${f.layoutGridColumnSpan}`
                      },
                      display: f.type === 'hidden' || !fieldIsVisible ? 'none' : 'block'
                    }}
                  >
                    {renderFieldContent(f)}
                  </Box>
                )
              })
            )
          }
          return jsxElements
        })()}

        <Box
          sx={{
            gridColumn: 'span 12',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Button
            type="submit"
            variant="contained"
            color={submit?.color || 'warning'}
            disabled={formik.isSubmitting}
            endIcon={formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            icon="user"
          >
            {submit.text}
          </Button>
        </Box>

        {statusMessage !== '' ? (
          <Box
            sx={{
              gridColumn: 'span 12',
              typography: 'textSmall',
              color: formik.status !== 'error' ? 'text.primary' : 'error.main'
            }}
          >
            {parse(statusMessage)}
          </Box>
        ) : null}
      </Box>
    </LocalizationProvider>
  )
}

export default Form
